import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { getUsers, getUserDetails } from '../../api/recommendationAPI';
import '../Profile.css';
import { ADMIN_EMAIL } from '../../constants';

const PAGE_SIZE = 100;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const UserDetailsPopup = ({ user, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOrderId, setOpenOrderId] = useState(null);

  useEffect(() => {
    getUserDetails(user.id)
      .then(setDetails)
      .catch(() => setError('שגיאה בטעינת פרטים'))
      .finally(() => setLoading(false));
  }, [user.id]);

  return (
    <div className="rec-popup-overlay" onClick={onClose}>
      <div className="user-details-popup" onClick={e => e.stopPropagation()}>
        <button className="rec-popup-close" onClick={onClose}>✕</button>

        <div className="user-details-header">
          <div className="user-details-name">{user.name}</div>
          <div className="user-details-email">{user.email}</div>
          {user.phone && <div className="user-details-phone">{user.phone}</div>}
        </div>

        {loading && <div className="history-status">טוען...</div>}
        {error && <div className="history-status" style={{ color: 'red' }}>{error}</div>}

        {details && (
          <div className="user-details-body">

            {/* הזמנות */}
            <div className="user-details-section-title">
              הזמנות ({details.orders?.length || 0})
            </div>
            {(!details.orders || details.orders.length === 0) ? (
              <div className="user-details-empty">אין הזמנות</div>
            ) : details.orders.map(order => (
              <div key={order.order_id} className="user-order-card">
                <div
                  className="user-order-header"
                  onClick={() => setOpenOrderId(openOrderId === order.order_id ? null : order.order_id)}
                >
                  <span className="user-order-id">הזמנה #{order.order_id}</span>
                  <span className="user-order-date">{formatDate(order.created_at)}</span>
                  {order.coupon_code && (
                    <span className="user-order-coupon">{order.coupon_code}</span>
                  )}
                  <span className="user-order-total">
                    ₪{Number(order.total_price).toFixed(2)}
                  </span>
                  <span className="history-expand-icon">
                    {openOrderId === order.order_id ? '▲' : '▼'}
                  </span>
                </div>

                {openOrderId === order.order_id && (
                  <div className="user-order-items">
                    {(order.items || []).map((item, idx) => (
                      <div key={item.item_id || idx} className="user-order-item">
                        <span className="user-order-item-name">{item.item_name}</span>
                        <span className="user-order-item-meta">
                          {item.quantity > 1 && `×${item.quantity} `}
                          ₪{Number(item.price).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {Number(order.coupon_discount) !== 0 && (
                      <div className="user-order-discount">
                        הנחה: ₪{Math.abs(Number(order.coupon_discount)).toFixed(2)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* קופונים */}
            <div className="user-details-section-title" style={{ marginTop: 20 }}>
              קופונים ({details.coupons?.length || 0})
            </div>
            {(!details.coupons || details.coupons.length === 0) ? (
              <div className="user-details-empty">אין קופונים</div>
            ) : (
              <div className="user-coupons-list">
                {details.coupons.map((c, idx) => (
                  <div key={c.id || idx} className="user-coupon-chip">
                    <span className="user-coupon-code">{c.coupon_code}</span>
                    <span className="user-coupon-discount">
                      {c.is_percentage ? `${c.discount_amount}%` : `₪${Number(c.discount_amount).toFixed(2)}`}
                    </span>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

const AdminUsers = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) { navigate('/', { replace: true }); return; }
    getUsers()
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setError('שגיאה בטעינת משתמשים'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageUsers = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  if (!user || user.email !== ADMIN_EMAIL) return null;

  return (
    <div className="history-page">
      <div className="users-toolbar">
        <input
          className="users-search-input"
          placeholder="חיפוש לפי שם או מייל..."
          value={search}
          onChange={handleSearch}
          dir="rtl"
        />
        <span className="users-count">{filtered.length} משתמשים</span>
      </div>

      {loading && <div className="history-status">טוען...</div>}
      {error && <div className="history-status" style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>שם</th>
                  <th>מייל</th>
                  {users[0]?.phone !== undefined && <th>טלפון</th>}
                  {users[0]?.created_at !== undefined && <th>הצטרף</th>}
                </tr>
              </thead>
              <tbody>
                {pageUsers.length === 0 ? (
                  <tr><td colSpan="5" className="users-empty-row">לא נמצאו משתמשים</td></tr>
                ) : pageUsers.map((u, idx) => (
                  <tr key={u.id || idx} className="users-table-row" onClick={() => setSelectedUser(u)}>
                    <td className="users-td-num">{u.id}</td>
                    <td>{u.name || '—'}</td>
                    <td className="users-td-email">{u.email || '—'}</td>
                    {users[0]?.phone !== undefined && <td>{u.phone || '—'}</td>}
                    {users[0]?.created_at !== undefined && (
                      <td>{formatDate(u.created_at)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="users-pagination">
              <button className="users-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>›</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`users-page-btn${currentPage === p ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="users-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>‹</button>
            </div>
          )}
        </>
      )}

      {selectedUser && (
        <UserDetailsPopup user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
};

export default AdminUsers;
